import { usePostHog } from '@posthog/react';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import CheckboxListInput from 'components/forms/inputs/rhf-inputs/CheckboxListInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import InfoTooltip from 'components/InfoTooltip';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCompleteCorrespondencesMutation } from 'store/api/correspondence';
import { CorrespondenceListElementDto } from 'types/correspondence';
import { errorAlert } from 'utils/alert';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

interface CompleteCorrespondenceForm {
    outgoingDate: string;
    markCompleted: boolean;
    selectedCorrespondenceIds: number[];
}

interface Props {
    correspondences: CorrespondenceListElementDto[];
    onClose: () => void;
}

const CompleteCorrespondenceModal: React.FC<Props> = ({ correspondences, onClose }) => {
    const posthog = usePostHog();
    const [completeCorrespondences, { isLoading }] = useCompleteCorrespondencesMutation();

    const methods = useForm<CompleteCorrespondenceForm>({
        defaultValues: {
            outgoingDate: formatDateStringInput(new Date()),
            markCompleted: true,
            selectedCorrespondenceIds: correspondences.map((c) => c.id),
        },
    });

    const { control, handleSubmit } = methods;

    const handleSave = async (data: CompleteCorrespondenceForm) => {
        try {
            await completeCorrespondences({
                correspondenceIds: data.selectedCorrespondenceIds,
                outgoingDate: data.outgoingDate,
            }).unwrap();
            posthog.capture('correspondence_completed', {
                correspondence_count: data.selectedCorrespondenceIds.length,
            });
            onClose();
        } catch {
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const onSave = async () => {
        await handleSubmit(handleSave)();
    };

    const correspondenceOptions = correspondences.map((c) => ({
        value: c.id,
        label: c.number,
    }));

    return (
        <FormProvider {...methods}>
            <ModalHeader title={t('correspondence.complete-correspondence')} onCancel={onClose} />
            <div className="overflow-y-auto p-6 max-sm:px-4">
                <div className="flex flex-col gap-4">
                    <p className="text-body1">{t('correspondence.complete-correspondence-confirmation-text')}</p>

                    <DateInput
                        name="outgoingDate"
                        control={control}
                        label="correspondence.outgoing-date"
                        tooltip={<InfoTooltip text="correspondence.tooltips.select-outgoing-date" />}
                    />

                    <CheckboxInput
                        name="markCompleted"
                        control={control}
                        label="correspondence.mark-correspondence-status-completed"
                        tooltip={
                            <InfoTooltip text="correspondence.tooltips.all-selected-correspondence-marked-completed" />
                        }
                        disabled
                    />

                    <CheckboxListInput
                        name="selectedCorrespondenceIds"
                        control={control}
                        label="correspondence.changes-following-correspondences"
                        options={correspondenceOptions}
                        labelLink={(value) => `/correspondence/${value}`}
                    />
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
                disableSave={isLoading}
            />
        </FormProvider>
    );
};

export default CompleteCorrespondenceModal;
